import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface CostBenefitAnalysisChartProps {
  data: Array<{
    workshop_id: number;
    workshop_title: string;
    investment_cost: number;
    estimated_savings: number;
    released_savings: number;
    roi: number;
    status: string;
  }>;
  loading?: boolean;
}

const CostBenefitAnalysisChart: React.FC<CostBenefitAnalysisChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost-Benefit Analysis</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost-Benefit Analysis</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No cost-benefit data available
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) {
      return `€${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `€${(value / 1000).toFixed(1)}K`;
    }
    return `€${value.toFixed(0)}`;
  };

  const getROIColor = (roi: number) => {
    if (roi >= 500) return '#10b981'; // Green for excellent ROI
    if (roi >= 200) return '#3b82f6'; // Blue for good ROI
    if (roi >= 100) return '#f59e0b'; // Yellow for average ROI
    return '#ef4444'; // Red for poor ROI
  };

  const getROILevel = (roi: number) => {
    if (roi >= 500) return 'Excellent';
    if (roi >= 200) return 'Good';
    if (roi >= 100) return 'Average';
    return 'Poor';
  };

  const maxInvestment = Math.max(...data.map(d => d.investment_cost));
  const maxSavings = Math.max(...data.map(d => d.estimated_savings));

  // Filter data for better visualization (limit to top performers and outliers)
  const filteredData = data.filter(item => 
    item.investment_cost > 0 && item.estimated_savings > 0
  ).slice(0, 50); // Limit to 50 workshops for better visualization

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost-Benefit Analysis</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              dataKey="investment_cost" 
              name="Investment Cost"
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrency}
              domain={[0, maxInvestment + 1000]}
            />
            <YAxis 
              type="number" 
              dataKey="estimated_savings" 
              name="Estimated Savings"
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrency}
              domain={[0, maxSavings + 1000]}
            />
            <Tooltip 
              cursor={{ strokeDasharray: '3 3' }}
              formatter={(value: number, name: string, props: any) => {
                if (name === 'investment_cost') return [formatCurrency(value), 'Investment Cost'];
                if (name === 'estimated_savings') return [formatCurrency(value), 'Estimated Savings'];
                if (name === 'released_savings') return [formatCurrency(value), 'Released Savings'];
                if (name === 'roi') return [`${value.toFixed(1)}%`, 'ROI'];
                return [value, name];
              }}
              labelFormatter={(label, payload) => {
                if (payload && payload[0]) {
                  const data = payload[0].payload;
                  return `Workshop: ${data.workshop_title}`;
                }
                return '';
              }}
            />
            <Scatter dataKey="estimated_savings" fill="#3b82f6">
              {filteredData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getROIColor(entry.roi)}
                  r={Math.max(4, Math.min(12, (entry.roi / 500) * 8 + 4))}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Top Performers</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => b.roi - a.roi)
              .slice(0, 5)
              .map((item) => {
                const level = getROILevel(item.roi);
                const color = getROIColor(item.roi);
                return (
                  <div key={item.workshop_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: color }}
                      ></div>
                      <span className="text-sm font-medium truncate">{item.workshop_title}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.roi.toFixed(1)}%</span>
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: color + '20', 
                        color: color 
                      }}>
                        {level}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Analysis Summary</h4>
          <div className="space-y-3">
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Best ROI</span>
                <span className="font-bold text-green-600">
                  {data.reduce((max, item) => item.roi > max.roi ? item : max, data[0])?.roi.toFixed(1)}%
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {data.reduce((max, item) => item.roi > max.roi ? item : max, data[0])?.workshop_title}
              </div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Total Investment</span>
                <span className="font-bold text-blue-600">
                  {formatCurrency(data.reduce((sum, item) => sum + item.investment_cost, 0))}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                Across {data.length} workshops
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-purple-600">Average ROI</span>
                <span className="font-bold text-purple-600">
                  {(data.reduce((sum, item) => sum + item.roi, 0) / data.length).toFixed(1)}%
                </span>
              </div>
              <div className="text-xs text-purple-500 mt-1">
                Overall performance
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostBenefitAnalysisChart;


